import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  Clock,
  FileText,
  FolderPlus,
  Loader2,
  Package,
  Pencil,
  Shield,
  Trash2,
  Upload,
  UserCheck,
  X,
  Zap,
} from "lucide-react";
import { listAudit } from "./api";
import { AuditEvent, ComplianceRun, VerityProject } from "./types";

interface AuditTrailProps {
  project: VerityProject;
  run: ComplianceRun | null;
  onClose: () => void;
}

/** One mark per kind of act, so the shape of a project's history is readable before the words are. */
const ICON: Record<string, typeof Shield> = {
  "project.created": FolderPlus,
  "project.updated": Pencil,
  "project.reviewers": UserCheck,
  "deliverable.added": FileText,
  "deliverable.parsed": Zap,
  "deliverable.removed": Trash2,
  // AWP projects keep packages where an engineering project keeps deliverables, and they move
  // through the same shape of history: added, given a document, read, checked.
  "package.added": Package,
  "package.updated": Pencil,
  "package.removed": Trash2,
  "package.document": Upload,
  "package.read": Zap,
  "compliance.run": Shield,
  "finding.decided": Check,
};

function when(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: "" };
  return {
    date: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
}

/**
 * The project's audit trail — who did what, and when.
 *
 * NOT A STAGE, which is why it is a panel rather than a step: a submission does not advance by
 * somebody reading its history, and the record is worth opening at any point rather than only at
 * the end. It used to be the fifth circle on the tracker, which made finishing the work look like
 * it meant finishing the reading.
 *
 * READ FROM A REAL EVENT LOG. Every line is a row something wrote at the moment it happened, with
 * the actor's name resolved then rather than now — see auditStore.ts. The panel used to derive its
 * entries from whatever records happened to carry a timestamp, which meant uploads and parses were
 * invisible because nothing had stored them.
 *
 * PROJECTS THAT PREDATE THE LOG still have a run and its decisions, and those are shown when there
 * are no events, marked as what they are. A project worked on before this existed should not read
 * as a project where nothing happened.
 */
const AuditTrail = ({ project, run, onClose }: AuditTrailProps) => {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  /**
   * Whether the log EXISTS, as distinct from whether it is empty.
   *
   * The container is created by a provisioning step, and an environment that has not run it reads
   * as a project where nothing happened. The first version of this panel told a project created
   * minutes earlier that it predated the event log — a confident wrong answer, and the one thing a
   * record must never give.
   */
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await listAudit(project.id);
      setEvents(result.items);
      setAvailable(result.available);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the audit trail.");
      setEvents([]);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** What a project from before the log can still show: the run, and each decision on it. */
  const derived = useMemo<AuditEvent[]>(() => {
    const out: AuditEvent[] = [];
    if (run) {
      out.push({
        id: `derived-run`,
        at: run.runAt,
        actorId: run.runBy,
        action: "compliance.run",
        summary: "Compliance check run",
        detail: `${run.findings.length} finding${run.findings.length === 1 ? "" : "s"} raised`,
      });
    }
    for (const finding of run?.findings ?? []) {
      if (!finding.reviewedAt) continue;
      const status = finding.status ?? "open";
      out.push({
        id: `derived-${finding.id}`,
        at: finding.reviewedAt,
        actorId: finding.reviewedBy,
        action: "finding.decided",
        summary: `${status === "accepted" ? "Accepted" : status === "dismissed" ? "Dismissed" : "Reopened"} finding: ${finding.title}`,
        detail: finding.reviewNote,
      });
    }
    // Oldest first, matching the recorded log — the two must not disagree about direction.
    return out.sort((a, b) => (a.at < b.at ? -1 : 1));
  }, [run]);

  const showing = events && events.length > 0 ? events : derived;
  const isDerived = !(events && events.length > 0) && derived.length > 0;

  return (
    <div className="vr-backdrop" onClick={onClose}>
      <div
        className="vr-modal vr-modal-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Audit trail"
      >
        <div className="vr-modal-head">
          <div>
            <h3>Audit Trail</h3>
            <p className="vr-muted">{project.name}</p>
          </div>
          <button className="vr-rowbtn vr-icononly" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        <div className="vr-modal-body">
          {error && <div className="vr-form-error">{error}</div>}

          {events === null ? (
            <div className="vr-empty">
              <Loader2 size={15} className="vr-spin-inline" /> Reading the record…
            </div>
          ) : showing.length === 0 ? (
            <div className="vr-empty">Nothing has happened to this project yet.</div>
          ) : (
            <div className="vr-audit-list">
              {showing.map((event) => {
                const Icon = ICON[event.action] ?? Clock;
                const stamp = when(event.at);
                return (
                  <div className={`vr-audit-row vr-audit-${event.action.split(".")[0]}`} key={event.id}>
                    <span className="vr-audit-icon">
                      <Icon size={13} />
                    </span>
                    <div className="vr-audit-body">
                      <p className="vr-audit-title">{event.summary}</p>
                      {event.detail && <p className="vr-audit-detail">{event.detail}</p>}
                      <p className="vr-audit-when">
                        <Clock size={11} />
                        {stamp.date} · {stamp.time}
                        {/* The name recorded at the time, falling back to the id when the log
                            predates names or Auth0 could not be reached that minute. */}
                        {event.actorName || event.actorId ? (
                          <span className="vr-audit-who">
                            {event.actorName ?? event.actorId}
                          </span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TWO REASONS FOR A SHORT LIST, AND THEY ARE NOT THE SAME. The store may not exist on
              this environment, or the project may simply be older than the log. Guessing between
              them told a project created minutes earlier that it predated its own history. */}
          {!available ? (
            <p className="vr-foot-note" style={{ marginTop: "1rem" }}>
              The event log is not set up on this environment, so nothing is being recorded. Anything
              shown above is read from the compliance run itself.
            </p>
          ) : isDerived ? (
            <p className="vr-foot-note" style={{ marginTop: "1rem" }}>
              This project predates the event log, so only its compliance run and the decisions made
              on it can be shown. Everything done from now on is recorded in full.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
